import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";

export default function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2.5 mb-10">
          <Logo size={32} />
          <span className="font-display font-black tracking-tight text-xl">ViralCrew</span>
        </Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-3">{t.auth.loginTitle}</h1>
          <p className="text-zinc-400 mb-8">{t.auth.loginSub}</p>

          <form onSubmit={submit} className="space-y-4">
            <input
              required
              type="email"
              placeholder={t.auth.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={LOGIN.emailInput}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition"
            />
            <input
              required
              type="password"
              placeholder={t.auth.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={LOGIN.passwordInput}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition"
            />
            {err && <p className="text-sm text-[#FF3B30]">{err}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid={LOGIN.submitButton}
              className="w-full bg-[#FF0050] hover:bg-[#E00046] disabled:opacity-60 text-white font-bold rounded-full px-6 py-3 transition-all hover:shadow-[0_0_30px_rgba(255,0,80,0.5)]"
            >
              {loading ? "..." : t.auth.submitLogin}
            </button>
          </form>

          <p className="text-zinc-500 text-sm mt-6">
            {t.auth.noAccount}{" "}
            <Link to="/#contact" data-testid={LOGIN.registerLink} className="text-white hover:text-[#FF0050] underline-offset-4 hover:underline">
              {t.auth.contactUs}
            </Link>
          </p>
        </motion.div>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#050505] text-white grid md:grid-cols-2">
      <div className="relative hidden md:flex items-center justify-center overflow-hidden border-r border-white/10">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-[#FF0050] opacity-30 blur-[150px] rounded-full" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#00F2FE] opacity-20 blur-[120px] rounded-full" />
        </div>
        <div className="relative z-10 px-12 max-w-md">
          <p className="text-xs font-mono uppercase tracking-wider text-[#00F2FE] mb-6">// ViralCrew</p>
          <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95] mb-6">
            {t.auth.side.titlePre}<span className="gradient-text">{t.auth.side.titleHighlight}</span>{t.auth.side.titlePost}
          </h2>
          <p className="text-zinc-400">{t.auth.side.sub}</p>
          <div className="mt-12 grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {[
              { v: "38.9K €", l: t.auth.side.statGmv },
              { v: "70+", l: t.auth.side.statCreators },
              { v: "92%", l: t.auth.side.statRetention },
            ].map((m) => (
              <div key={m.l} className="bg-[#0A0A0A] p-4 text-center">
                <div className="font-display font-black text-xl tracking-tighter">{m.v}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">{children}</div>
    </div>
  );
}
